<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useOrgStore } from '@/stores/org'
import { useUserStore } from '@/stores/user'
import { useAccessStore } from '@/stores/access'
import { UserPlus, Loader2, ChevronDown, ChevronRight, MoreHorizontal, RefreshCw, UserMinus, Banknote } from 'lucide-vue-next'
import KycStatusDisplay from './KycStatusDisplay.vue'
import MemberKycSteps from './MemberKycSteps.vue'
import PayMemberDialog from './PayMemberDialog.vue'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from '@/components/ui/dropdown-menu'
import DialogConfirm from '@/components/dialogs/DialogConfirm.vue'
import { API, HOST } from '@/utils/http'
import { useToast } from '@/components/ui/toast/use-toast'
import * as Sentry from '@sentry/vue'
import { isSandboxEnvironment } from '@/config/bridge'
import type { BridgeCustomer } from '@/types/bridge'
import type OrgMember from '@/types/OrgMember'
import { can } from '@/composables/useCan'

const { toast } = useToast()

const props = defineProps<{ orgId: number }>()

const payDialogOpen = ref(false)
const payTarget = ref<{ id: number; name: string } | null>(null)

const openPayDialog = (bridge: BridgeCustomer) => {
  payTarget.value = {
    id: bridge.id,
    name: bridge.member_display_name || bridge.member_email || `Member #${bridge.member_id}`,
  }
  payDialogOpen.value = true
}

const orgStore = useOrgStore()

const userStore = useUserStore()

const accessStore = useAccessStore()
const member = computed(() => userStore.member)
const isSuperUser = computed(() => member.value?.is_super_user)
const isSandbox = isSandboxEnvironment()

const bridgeCustomers = ref<BridgeCustomer[]>([])
const bridgeLoading = ref(false)
const enrolling = ref<Set<number>>(new Set())
const syncing = ref<Set<number>>(new Set())
const unenrolling = ref<Set<number>>(new Set())
const expandedRows = ref<Set<number>>(new Set())
const confirmUnenrollDialog = ref<InstanceType<typeof DialogConfirm> | null>(null)
const pendingUnenroll = ref<BridgeCustomer | null>(null)
const confirmEnrollDialog = ref<InstanceType<typeof DialogConfirm> | null>(null)
const pendingEnroll = ref<OrgMember | null>(null)

const orgMembers = computed<OrgMember[]>(() => orgStore.currentMembers ?? [])
const orgName = computed(() => orgStore.getSelectedOrg?.organization_name ?? '')
const membersLoading = computed(() => !orgMembers.value.length)

const roleName = (m: OrgMember) => accessStore.role(m.role_id)?.name ?? ''

const bridgeByMemberId = computed(() => {
  const map = new Map<number, BridgeCustomer>()
  for (const c of bridgeCustomers.value) {
    map.set(c.member_id, c)
  }
  return map
})

const statusRank = (bridge: BridgeCustomer | null): number => {
  if (!bridge) return 3
  const s = kycStatus(bridge)
  if (s === 'approved') return 0
  if (s === 'in_progress') return 1
  return 2
}

const rows = computed(() => {
  return orgMembers.value
    .map(m => ({ member: m, bridge: bridgeByMemberId.value.get(m.member_id) ?? null }))
    .sort((a, b) => {
      const rankDiff = statusRank(a.bridge) - statusRank(b.bridge)
      if (rankDiff !== 0) return rankDiff
      return (a.member.display_name || '').localeCompare(b.member.display_name || '')
    })
})

const kycStatus = (customer: BridgeCustomer): 'approved' | 'rejected' | 'in_progress' | 'awaiting_ubo' => {
  const kyc = customer.kyc_status
  if (kyc === 'approved') return 'approved'
  if (['rejected', 'paused', 'offboarded'].includes(kyc)) return 'rejected'
  if (kyc === 'awaiting_ubo') return 'awaiting_ubo'
  return 'in_progress'
}

const isExpandable = (bridge: BridgeCustomer | null) =>
  bridge !== null && kycStatus(bridge) !== 'approved'

const toggleRow = (memberId: number) => {
  if (expandedRows.value.has(memberId)) {
    expandedRows.value.delete(memberId)
  } else {
    expandedRows.value.add(memberId)
  }
}

const onVisibilityChange = () => {
  if (document.visibilityState === 'visible') {
    fetchBridgeCustomers()
  }
}


const fetchBridgeCustomers = async () => {
  bridgeLoading.value = true
  try {
    const url = isSuperUser.value
      ? `${HOST}/payments/bridge/customers`
      : `${HOST}/payments/bridge/organizations/${props.orgId}/member-customers`
    const response = await API({ method: 'GET', url })
    bridgeCustomers.value = response.data
  } finally {
    bridgeLoading.value = false
  }
}

const syncCustomer = async (bridge: BridgeCustomer) => {
  syncing.value.add(bridge.id)
  try {
    await API.post(`${HOST}/payments/bridge/customers/${bridge.id}/sync`)
    await fetchBridgeCustomers()
  } catch (err: any) {
    const msg = err.response?.data?.detail || 'Failed to sync KYC status'
    toast({ title: 'Sync failed', description: msg, variant: 'destructive' })
    Sentry.captureException(err, { extra: { bridge_customer_id: bridge.id } })
  } finally {
    syncing.value.delete(bridge.id)
  }
}

const unenroll = (bridge: BridgeCustomer) => {
  pendingUnenroll.value = bridge
  confirmUnenrollDialog.value?.open()
}

const confirmUnenroll = async () => {
  const bridge = pendingUnenroll.value
  if (!bridge) return
  unenrolling.value.add(bridge.id)
  try {
    await API.delete(`${HOST}/payments/bridge/customers/${bridge.id}`)
    await fetchBridgeCustomers()
  } catch (err: any) {
    const msg = err.response?.data?.detail || 'Failed to unenroll member'
    toast({ title: 'Unenroll failed', description: msg, variant: 'destructive' })
    Sentry.captureException(err, { extra: { bridge_customer_id: bridge.id } })
  } finally {
    unenrolling.value.delete(bridge.id)
    pendingUnenroll.value = null
  }
}

const enroll = (member: OrgMember) => {
  pendingEnroll.value = member
  confirmEnrollDialog.value?.open()
}

const confirmEnroll = async (directEnroll = false) => {
  const member = pendingEnroll.value
  if (!member) return
  enrolling.value.add(member.member_id)
  try {
    await API({
      method: 'POST',
      url: `${HOST}/payments/bridge/customers`,
      data: { member_id: member.member_id, organization_id: props.orgId, direct_enroll: directEnroll }
    })
    await fetchBridgeCustomers()
  } catch (err: any) {
    const msg = err.response?.data?.detail || 'Failed to enroll member'
    toast({ title: 'Enroll failed', description: msg, variant: 'destructive' })
    Sentry.captureException(err, { extra: { member_id: member.member_id } })
  } finally {
    enrolling.value.delete(member.member_id)
    pendingEnroll.value = null
  }
}

const getInitials = (m: OrgMember) => {
  if (m.first_name && m.last_name) return `${m.first_name[0]}${m.last_name[0]}`.toUpperCase()
  return (m.display_name || m.email || '?').slice(0, 2).toUpperCase()
}

onMounted(async () => {
  orgStore.fetchOrgMembers(props.orgId)
  fetchBridgeCustomers()
  document.addEventListener('visibilitychange', onVisibilityChange)
})

onUnmounted(() => {
  document.removeEventListener('visibilitychange', onVisibilityChange)
})
</script>

<template>
  <div class="space-y-3">
    <div class="flex items-center justify-between">
      <h3 class="text-sm font-semibold">{{ orgName ? `${orgName} Members` : 'Members' }}</h3>
      <span v-if="!membersLoading" class="text-xs text-muted-foreground">{{ rows.length }} total</span>
    </div>

    <p v-if="can('org_member', 'update', { orgId })" class="text-xs text-muted-foreground" data-testid="billing-admin-hint">
      To let someone see invoices, give them the Billing admin role in
      <router-link :to="{ name: 'OrgMembers', params: { org_id: orgId } }" class="underline">Members</router-link>.
    </p>

    <!-- Loading -->
    <div v-if="membersLoading || bridgeLoading" class="space-y-2">
      <div v-for="i in 4" :key="i" class="h-12 bg-muted rounded animate-pulse" />
    </div>

    <!-- Table -->
    <div v-else class="border rounded-lg overflow-x-auto">
      <table class="w-full text-sm">
        <thead class="border-b bg-muted/50">
          <tr>
            <th class="w-8"></th>
            <th class="px-4 py-3 text-left font-medium text-muted-foreground">Member</th>
            <th class="px-4 py-3 text-center font-medium text-muted-foreground">KYC Status</th>
            <th class="px-4 py-3 text-left font-medium text-muted-foreground">Org Role</th>
            <th class="w-10"></th>
            <th class="w-10"></th>
          </tr>
        </thead>
        <tbody>
          <template v-for="{ member, bridge } in rows" :key="member.member_id">

            <!-- Main row -->
            <tr
              class="border-b hover:bg-muted/30 transition-colors"
              :class="[expandedRows.has(member.member_id) ? 'bg-muted/20' : '', isExpandable(bridge) ? 'cursor-pointer' : '']"
              @click="isExpandable(bridge) && toggleRow(member.member_id)"
            >
              <!-- Expand toggle -->
              <td class="pl-3 py-3 text-center">
                <button
                  v-if="isExpandable(bridge)"
                  @click.stop="toggleRow(member.member_id)"
                  class="text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                >
                  <ChevronDown v-if="expandedRows.has(member.member_id)" class="h-4 w-4" />
                  <ChevronRight v-else class="h-4 w-4" />
                </button>
              </td>

              <!-- Member -->
              <td class="px-4 py-3">
                <div class="flex items-center gap-2">
                  <Avatar class="h-7 w-7 flex-shrink-0">
                    <AvatarImage v-if="member.avatar_url" :src="member.avatar_url" />
                    <AvatarFallback class="text-xs">{{ getInitials(member) }}</AvatarFallback>
                  </Avatar>
                  <span class="font-medium truncate">{{ member.display_name || 'Unnamed' }}</span>
                </div>
              </td>

              <!-- KYC Status -->
              <td class="px-4 py-3 text-center">
                <KycStatusDisplay
                  v-if="bridge"
                  :kyc-status="kycStatus(bridge)"
                  :raw-kyc-status="bridge.kyc_status"
                  :tos-status="bridge.tos_status"
                  :customer-id="bridge.id"
                  :bridge-customer-id="bridge.bridge_customer_id"
                />
                <Button
                  v-else
                  size="sm"
                  variant="outline"
                  :disabled="enrolling.has(member.member_id)"
                  @click.stop="enroll(member)"
                >
                  <Loader2 v-if="enrolling.has(member.member_id)" class="h-3 w-3 mr-1 animate-spin" />
                  <UserPlus v-else class="h-3 w-3 mr-1" />
                  Enroll
                </Button>
              </td>

              <!-- Org Role -->
              <td class="px-4 py-3">
                <span class="text-xs text-muted-foreground">{{ roleName(member) }}</span>
              </td>

              <!-- Pay -->
              <td class="py-3 text-center" @click.stop>
                <Button
                  v-if="bridge && kycStatus(bridge) === 'approved'"
                  variant="ghost"
                  size="sm"
                  class="h-7 w-7 p-0"
                  title="Pay member"
                  @click="openPayDialog(bridge)"
                >
                  <Banknote class="h-4 w-4" />
                </Button>
              </td>

              <!-- Actions -->
              <td class="pr-2 py-3 text-right" @click.stop>
                <DropdownMenu v-if="bridge">
                  <DropdownMenuTrigger as-child>
                    <Button variant="ghost" size="sm" class="h-7 w-7 p-0">
                      <MoreHorizontal class="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem @click="syncCustomer(bridge)" :disabled="syncing.has(bridge.id)">
                      <RefreshCw class="h-3.5 w-3.5 mr-2" :class="{ 'animate-spin': syncing.has(bridge.id) }" />
                      Refresh
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      @click="unenroll(bridge)"
                      :disabled="unenrolling.has(bridge.id)"
                      class="text-red-600 focus:text-red-600"
                    >
                      <UserMinus class="h-3.5 w-3.5 mr-2" />
                      Unenroll
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </td>
            </tr>

            <!-- Expanded row -->
            <tr v-if="expandedRows.has(member.member_id)" class="border-b bg-slate-50 dark:bg-slate-900">
              <td colspan="6" class="px-6 py-4">
                <MemberKycSteps
                  :link="bridge?.link ?? null"
                  @refresh="fetchBridgeCustomers"
                />
              </td>
            </tr>

          </template>
        </tbody>
      </table>
    </div>
  </div>

  <PayMemberDialog
    v-if="payTarget"
    v-model:open="payDialogOpen"
    :org-id="orgId"
    :member-name="payTarget.name"
    :receiver-customer-id="payTarget.id"
    @paid="fetchBridgeCustomers"
  />

  <DialogConfirm
    ref="confirmEnrollDialog"
    title="Enroll member"
    :description="pendingEnroll ? `Enroll ${pendingEnroll.display_name ?? pendingEnroll.email ?? 'this member'} in Bridge payments?` : ''"
    confirm-text="Enroll"
    :secondary-text="isSandbox ? 'Admin Enroll' : ''"
    @confirm="confirmEnroll(false)"
    @secondary="confirmEnroll(true)"
  />

  <DialogConfirm
    ref="confirmUnenrollDialog"
    title="Unenroll member"
    :description="pendingUnenroll ? `Remove ${pendingUnenroll.member_display_name ?? pendingUnenroll.member_email ?? 'this member'} from Bridge payments? They can be re-enrolled later if they have no transfers.` : ''"
    confirm-text="Unenroll"
    variant="error"
    @confirm="confirmUnenroll"
  />
</template>
